/**
 * Section Merging
 * 
 * Merges base page sections (from pages.json) with dynamic section overrides
 * (from the database/baked overrides). This enables blocks to be added to
 * pages without modifying theme code.
 * 
 * Also handles grid group rendering -- blocks that share a gridGroup key
 * are grouped together for side-by-side layout.
 */

import type { PageSection, ComponentSlot } from './types';

// ============================================================================
// Section Override Record (runtime representation)
// ============================================================================

/**
 * A section override record as consumed by the runtime.
 * Matches the baked output format from vibesites-overrides.ts.
 */
export interface SectionOverrideRecord {
  /** Unique section instance ID */
  sectionId: string;
  /** Block catalog ID (e.g., 'iframe-embed') */
  blockId: string;
  /** Insertion position in the page sections array */
  position: number;
  /** Whether this section is visible (default true) */
  visible?: boolean;
  /** Grid group key for side-by-side layout (null = standalone full-width) */
  gridGroup?: string | null;
  /** Column span out of 12 (default 12 = full width) */
  gridSpan?: number;
  /** Order within the grid group */
  gridOrder?: number;
  /** Column span on tablet (null = same as desktop) */
  gridSpanTablet?: number | null;
  /** Column span on mobile (null = stack to 12) */
  gridSpanMobile?: number | null;
}

/**
 * Section overrides organized by page alias.
 */
export type SectionOverridesMap = Record<string, SectionOverrideRecord[]>;

/**
 * An extended PageSection that carries block-specific metadata.
 * Used after merging to provide grid layout information to the renderer.
 */
export interface MergedPageSection extends PageSection {
  /** Whether this section is a block (vs. a standard theme section) */
  isBlock?: boolean;
  /** Grid group key (if this block is part of a side-by-side layout) */
  gridGroup?: string | null;
  /** Column span out of 12 */
  gridSpan?: number;
  /** Order within the grid group */
  gridOrder?: number;
  /** Column span on tablet */
  gridSpanTablet?: number | null;
  /** Column span on mobile */
  gridSpanMobile?: number | null;
}

// ============================================================================
// Section Merging Logic
// ============================================================================

/**
 * Merge base page sections with section overrides.
 * 
 * Section overrides (blocks) are inserted at their specified positions
 * in the base sections array. Invisible overrides are excluded.
 * 
 * @param baseSections - The base sections from pages.json
 * @param overrides - Section override records for this page
 * @returns Merged array of sections with block metadata
 */
export function mergeSections(
  baseSections: PageSection[],
  overrides: SectionOverrideRecord[]
): MergedPageSection[] {
  // Start with base sections (marked as non-blocks)
  const merged: MergedPageSection[] = baseSections.map(s => ({ ...s }));

  // Sort overrides by position for deterministic insertion
  const sortedOverrides = [...overrides].sort((a, b) => a.position - b.position);

  for (const override of sortedOverrides) {
    // Skip invisible overrides
    if (override.visible === false) continue;

    const section: MergedPageSection = {
      id: override.sectionId,
      slot: `block:${override.blockId}` as ComponentSlot,
      isBlock: true,
      gridGroup: override.gridGroup ?? null,
      gridSpan: override.gridSpan ?? 12,
      gridOrder: override.gridOrder ?? 0,
      gridSpanTablet: override.gridSpanTablet ?? null,
      gridSpanMobile: override.gridSpanMobile ?? null,
    };

    // Insert at the specified position (clamped to bounds)
    const pos = Math.min(Math.max(0, override.position), merged.length);
    merged.splice(pos, 0, section);
  }

  return merged;
}

// ============================================================================
// Grid Group Helpers
// ============================================================================

/**
 * A group of sections that should be rendered side-by-side in a grid row.
 */
export interface GridGroup {
  /** The grid group key */
  key: string;
  /** Sections in this group, sorted by gridOrder */
  sections: MergedPageSection[];
}

/**
 * Organize merged sections into a render-ready structure that handles
 * both standalone sections and grid groups.
 * 
 * Returns an array of "render items" where each item is either:
 * - A standalone section (rendered full-width)
 * - A grid group (multiple sections rendered side-by-side)
 */
export type RenderItem =
  | { type: 'section'; section: MergedPageSection }
  | { type: 'grid-group'; group: GridGroup };

export function organizeForRendering(sections: MergedPageSection[]): RenderItem[] {
  const items: RenderItem[] = [];
  const gridGroupBuffer: Map<string, MergedPageSection[]> = new Map();
  const gridGroupInsertOrder: string[] = [];

  for (const section of sections) {
    if (section.gridGroup) {
      // Part of a grid group
      if (!gridGroupBuffer.has(section.gridGroup)) {
        gridGroupBuffer.set(section.gridGroup, []);
        gridGroupInsertOrder.push(section.gridGroup);
        // Insert a placeholder for where this grid group first appeared
        items.push({
          type: 'grid-group',
          group: { key: section.gridGroup, sections: [] },
        });
      }
      gridGroupBuffer.get(section.gridGroup)!.push(section);
    } else {
      // Standalone section
      items.push({ type: 'section', section });
    }
  }

  // Fill in the grid group placeholders with sorted sections
  for (const item of items) {
    if (item.type === 'grid-group') {
      const sections = gridGroupBuffer.get(item.group.key) || [];
      item.group.sections = sections.sort((a, b) => (a.gridOrder ?? 0) - (b.gridOrder ?? 0));
    }
  }

  return items;
}
